/**
 * Hook del listado de Cargadores con paginación server-side (US-2.2).
 *
 * Espejo de `useLocations`: filtros + `offset`/`limit` + `meta` del backend, con
 * loading/error/refresh. La red vive en `stationApi`; aquí solo el estado de la
 * vista (separable de la presentación).
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  AdminStationSummary,
  ListStationsQuery,
  PageMeta,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { stationApi, type StationListParams } from '../api/stationApi';

/** Límite por página por defecto del Admin Web. */
export const DEFAULT_STATIONS_LIMIT = 20;

/** Filtros del listado (los del contrato, sin paginación). `status` incluye `ALL`. */
export type StationFilters = Omit<ListStationsQuery, 'offset' | 'limit'>;

export interface UseStationsOptions {
  /** Elementos por página (default `DEFAULT_STATIONS_LIMIT`). */
  limit?: number;
  /** Filtros iniciales. */
  initialFilters?: StationFilters;
}

export interface UseStationsResult {
  /** Items resumidos del listado (con la locación proyectada). */
  data: AdminStationSummary[];
  meta: PageMeta;
  loading: boolean;
  error: string | null;
  /** Filtros activos. */
  filters: StationFilters;
  /** Desplazamiento actual (paginación server-side). */
  offset: number;
  limit: number;
  /** Aplica filtros nuevos y reinicia la paginación a la primera página. */
  setFilters: (filters: StationFilters) => void;
  /** Mueve la página (offset) actual. */
  setOffset: (offset: number) => void;
  /** Recarga la página actual (p. ej. tras crear/editar/eliminar un cargador). */
  refresh: () => void;
}

const emptyMeta = (limit: number): PageMeta => ({ offset: 0, limit, total: 0, hasMore: false });

function toErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudieron cargar los cargadores.';
}

export function useStations(options: UseStationsOptions = {}): UseStationsResult {
  const limit = options.limit ?? DEFAULT_STATIONS_LIMIT;
  const [filters, setFiltersState] = useState<StationFilters>(() => options.initialFilters ?? {});
  const [offset, setOffsetState] = useState(0);
  const [data, setData] = useState<AdminStationSummary[]>([]);
  const [meta, setMeta] = useState<PageMeta>(() => emptyMeta(limit));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: StationListParams = { ...filters, offset, limit };
    stationApi
      .list(params)
      .then((page) => {
        if (cancelled) return;
        setData(page.data);
        setMeta(page.meta);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(toErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Descarta respuestas de una página/filtro anterior que lleguen fuera de orden.
    return () => {
      cancelled = true;
    };
  }, [filters, offset, limit, reloadKey]);

  const setFilters = useCallback((next: StationFilters) => {
    setOffsetState(0);
    setFiltersState(next);
  }, []);

  const setOffset = useCallback((next: number) => {
    setOffsetState(next < 0 ? 0 : next);
  }, []);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  return { data, meta, loading, error, filters, offset, limit, setFilters, setOffset, refresh };
}
